import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Flex, List, Modal, Tag, Tooltip, Typography } from 'antd'
import { useIntl } from 'react-intl'
import Button from '../../../common/Button'
import EllipsisTooltip from '../../../common/EllipsisTooltip'
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'

// Single-line strip of the saved sales codes (ellipsis on overflow) with a
// "Details" button that opens the full list. Same pattern as the selected DTCs
// in dtcs/TATMappings/EditDtcsMappingHeader.
const SelectedSalesCodes = ({ salesCodes = [] }) => {
  const intl = useIntl()
  const timeoutRef = useRef()
  const container = useRef(null)
  const wrapper = useRef(null)
  const [overflow, setOverflow] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [sortOrder, setSortOrder] = useState(null)

  useEffect(() => {
    if (!container.current || !wrapper.current) return
    
    const elementContainer = container.current
    const elementWrapper = wrapper.current

    const observer = new ResizeObserver(() => {
      clearTimeout(timeoutRef.current)
      elementWrapper.classList.remove('hide-overflow')

      timeoutRef.current = setTimeout(() => {
        const hasOverflow =
          elementContainer.scrollWidth > elementContainer.clientWidth
        elementWrapper.classList.toggle('hide-overflow', hasOverflow)
        setOverflow(hasOverflow)
      }, 100)
    })

    observer.observe(elementContainer)

    return () => {
      clearTimeout(timeoutRef.current)
      observer.disconnect()
    }
  }, [salesCodes])

  const sortedData = useMemo(() => {
    if (!sortOrder) return salesCodes

    return [...salesCodes].sort((a, b) => {
      return sortOrder === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    })
  }, [salesCodes, sortOrder])

  const toggleSort = () => {
    setSortOrder((prev) => {
      switch (prev) {
        case null:
          return 'asc'
        case 'asc':
          return 'desc'
        default:
          return null
      }
    })
  }

  return (
    <>
      <Flex
        align="center"
        gap={16}
        className="sales-codes-list"
        data-testid="fd-saved-sales-codes"
      >
        <Flex
          ref={container}
          align="center"
          wrap="nowrap"
          gap={5}
          className="sales-codes-container"
        >
          {salesCodes.length ? (
            <div ref={wrapper} className="sales-codes-wrapper">
              {salesCodes.map((code) => (
                <Tooltip key={code.id} title={`${code.name} — ${code.description || ''}`}>
                  <Tag key={code.id}>
                    {code.name}
                  </Tag>
                </Tooltip>
              ))}
            </div>
          ) : (
            <Typography.Text type="secondary">
              {intl.formatMessage({ id: 'functionalDiagram.noSalesCodes' })}
            </Typography.Text>
          )}
          {overflow && (
            <Typography.Paragraph className="ellipsis">...</Typography.Paragraph>
          )}
        </Flex>

        {salesCodes.length > 0 && (
          <Button onClick={() => setShowDetails(true)} testId="fd-sales-codes-details">
            {intl.formatMessage({ id: 'generic.details', defaultMessage: 'Details' })}
          </Button>
        )}
      </Flex>

      {showDetails && (
        <Modal
          open={showDetails}
          title={intl.formatMessage(
            { id: 'functionalDiagram.selectedSalesCodeCount' },
            { count: salesCodes.length }
          )}
          width="60%"
          className="sales-codes-details-modal"
          centered={true}
          footer={null}
          closable={true}
          onCancel={() => setShowDetails(false)}
        >
          <Flex vertical>
            <Flex
              align="center"
              gap={8}
              className="sorting-column-header scrollable-list"
              onClick={toggleSort}
            >
              <Typography.Text>{intl.formatMessage({ id: 'functionalDiagram.selectedSalesCodes' })}</Typography.Text>
              <Flex vertical gap={0} className="sort-icon">
                <CaretUpOutlined className={sortOrder === 'asc' ? 'active' : ''} />
                <CaretDownOutlined
                  className={sortOrder === 'desc' ? 'active' : ''}
                />
              </Flex>
            </Flex>
            <List
              bordered
              dataSource={sortedData}
              className="sales-codes"
              renderItem={(code) => (
                <List.Item key={code.id}>
                  <Flex vertical gap={0}>
                    <Typography.Text strong>{code.name}</Typography.Text>
                    <Typography.Text>
                      {code.description || ''}
                      {code.group?.name ? ` (${code.group.name})` : ''}
                    </Typography.Text>
                  </Flex>
                </List.Item>
              )}
            />
          </Flex>
        </Modal>
      )}
    </>
  )
}

SelectedSalesCodes.propTypes = {
  salesCodes: PropTypes.array,
}

export default SelectedSalesCodes
